import { api } from "@/shared/api";
import { API_ENDPOINTS } from '@/shared/api';

export interface AccountModule {
  id: string;
  accountId: string;
  moduleId: string;
  module: {
    id: string;
    name: string;
  };
  isActive: boolean;
}

export interface Permission {
  id: string;
  action: string;
  subject: string;
  condition?: Record<string, unknown> | null;
}

export interface Policy {
  id: string;
  name: string;
  description?: string;
  permissions: Permission[];
}

export interface UserPolicy {
  id: string;
  policy: Policy;
  createdAt: string;
}

export interface AccountDetails {
  id: string;
  name: string;
  isActive: boolean;
  isDemo?: boolean;
  demoDataLoadedAt?: string | null;
  accountModules: AccountModule[];
}

export interface SubjectAction {
  subject: string;
  action: string;
}

export interface UserScope {
  account: AccountDetails | null;
  policies: UserPolicy[];
  enabledModules: string[]; // Module names
  allowedSubjects: string[]; // Subjects from permissions
  /** Allowed (subject, action) pairs for action-level checks (e.g. show Add/Edit/Delete) */
  allowedPermissions: SubjectAction[];
}

export const userScopeApi = {
  /**
   * Fetch account details with modules
   * Uses /accounts/me endpoint for tenant users
   */
  async getAccountDetails(accountId?: string): Promise<AccountDetails> {
    // Use /me endpoint if no accountId provided (for tenant users)
    const endpoint = accountId ? API_ENDPOINTS.ACCOUNTS.detail(accountId) : API_ENDPOINTS.ACCOUNTS.me();
    const response = await api.get<AccountDetails>(endpoint);
    return response.data;
  },

  /**
   * Fetch user policies with permissions using the /me/policies endpoint
   */
  async getUserPolicies(): Promise<UserPolicy[]> {
    const response = await api.get<UserPolicy[]>(API_ENDPOINTS.POLICIES.mine());
    return response.data;
  },

  /**
   * Build user scope from account and policies
   */
  async buildUserScope(accountId?: string): Promise<UserScope> {
    const [account, policies] = await Promise.all([
      this.getAccountDetails(accountId),
      this.getUserPolicies(),
    ]);

    // Extract enabled module names
    const enabledModules = account.accountModules
      .filter(am => am.isActive)
      .map(am => am.module.name);

    // Extract allowed subjects and (subject, action) pairs from permissions
    const allowedSubjects = new Set<string>();
    const allowedPermissions: SubjectAction[] = [];

    policies.forEach(userPolicy => {
      userPolicy.policy.permissions.forEach(permission => {
        if (permission.subject !== 'all') {
          allowedSubjects.add(permission.subject);
        } else {
          allowedSubjects.add('all');
        }
        allowedPermissions.push({ subject: permission.subject, action: permission.action });
      });
    });

    return {
      account,
      policies,
      enabledModules,
      allowedSubjects: Array.from(allowedSubjects),
      allowedPermissions,
    };
  },
};

